import { useEffect, useState } from "react";
import { createApiClient, type ApiClient } from "../../api/client";
import { t } from "../../i18n";
import { appDevice, appSession } from "../app-storage";
import { allowNotifications, backgroundLocation, nativeFetch } from "../native";

/**
 * The courier's phone tells Tafo where he is, only during a ride (voorwaarden 7.4): from "🛵 Vertrek"
 * until he is back at the shop after his last delivery (Furkan, 18/10/2026). The shop sees his rides
 * arrive and when he is back, the customer sees how far he is. Outside a ride the phone is not asked
 * anything.
 *
 * In the browser this works while the app is on the screen; the phone stops telling when the screen goes
 * dark or the courier opens Google Maps. The app from the stores (mobile/, ADR 0032) tells on in the
 * background, with a notification that says so, and sends through the phone's own connection.
 */

/** What the app shows the courier about it. */
export type Sharing =
  /** No ride on the way: nothing is asked. */
  | "off"
  /** Asked, no position yet (the phone is looking for satellites, or the courier did not answer yet). */
  | "looking"
  /** The server has his position. */
  | "on"
  /** The courier or the phone said no. */
  | "denied"
  /** This phone or browser cannot tell a position. */
  | "unavailable";

export interface Fix {
  latitude: number;
  longitude: number;
  accuracyM: number;
}

/** Not more often than this, however fast the phone reports. */
export const MIN_SECONDS = 5;
/** Standing still: say so once in a while, so the server knows the phone is still there. */
export const STILL_SECONDS = 45;
/** Moving: every time he is this far from where he last told. */
export const MOVED_METRES = 30;

/** Metres between two positions close to each other (a flat map is exact enough within a town). */
export function metresBetween(a: Fix, b: Fix): number {
  const north = (b.latitude - a.latitude) * 111_320;
  const east = (b.longitude - a.longitude) * 111_320 * Math.cos((a.latitude * Math.PI) / 180);
  return Math.hypot(north, east);
}

/** Whether this position is worth telling: the first one, after he moved, or to say he is still there. */
export function worthTelling(last: { fix: Fix; at: number } | null, fix: Fix, now: number): boolean {
  if (last === null) return true;
  const seconds = (now - last.at) / 1000;
  if (seconds < MIN_SECONDS) return false;
  return seconds >= STILL_SECONDS || metresBetween(last.fix, fix) >= MOVED_METRES;
}

/**
 * Shares the position while `riding`. Returns what to show the courier. Stops by itself when the server
 * says there is no ride on the way any more.
 */
export function useSharePosition(api: ApiClient, riding: boolean): Sharing {
  /** What the phone and the server said during this ride; "looking" until one of them did. */
  const [heard, setHeard] = useState<"looking" | "on" | "denied" | "stopped">("looking");
  const native = backgroundLocation();
  const able = native !== null || (typeof navigator !== "undefined" && "geolocation" in navigator);

  useEffect(() => {
    if (!riding || !able) return;
    let stopped = false;
    const plugin = backgroundLocation();
    // in the app from the stores: through the phone's own connection, which Android does not slow down
    const fetchImpl = nativeFetch();
    const sender =
      fetchImpl === null
        ? api
        : createApiClient(
            { deviceToken: () => appDevice.read(), staffToken: () => appSession.read()?.token ?? null },
            fetchImpl,
          );
    let last: { fix: Fix; at: number } | null = null;
    let latest: Fix | null = null;
    let sending = false;

    const tell = (fix: Fix) => {
      if (stopped || sending) return;
      sending = true;
      last = { fix, at: Date.now() };
      sender
        .request<{ sharing: boolean }>("POST", "/device/rides/position", { body: fix })
        .then((answer) => {
          if (stopped) return;
          // no ride on the way any more (delivered at the till, cancelled): stop asking the phone
          if (answer.sharing) setHeard("on");
          else {
            stop();
            setHeard("stopped");
          }
        })
        // out of reach for a moment: the next position tries again; nothing is queued
        .catch(() => undefined)
        .finally(() => {
          sending = false;
        });
    };

    const heardFix = (fix: Fix) => {
      latest = fix;
      if (worthTelling(last, fix, Date.now())) tell(fix);
    };
    let watch: number | null = null;
    let nativeWatch: Promise<string> | null = null;
    if (plugin !== null) {
      // the notification of Android 13+ needs permission, or the position stops in the background
      void allowNotifications().then(() => {
        if (stopped) return;
        nativeWatch = plugin.addWatcher(
          {
            backgroundTitle: t.tafoApp.courier.background.title,
            backgroundMessage: t.tafoApp.courier.background.message,
            requestPermissions: true,
            stale: false,
            distanceFilter: MOVED_METRES,
          },
          (position, error) => {
            if (stopped) return;
            if (error?.code === "NOT_AUTHORIZED") setHeard("denied");
            if (position === undefined) return;
            heardFix({
              latitude: position.latitude,
              longitude: position.longitude,
              accuracyM: Math.round(position.accuracy),
            });
          },
        );
        nativeWatch.catch(() => undefined);
      });
    } else {
      watch = navigator.geolocation.watchPosition(
        (position) => {
          heardFix({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracyM: Math.round(position.coords.accuracy),
          });
        },
        (error) => {
          if (stopped) return;
          // 1 = the courier or the phone refused; anything else is a moment without satellites
          if (error.code === 1) setHeard("denied");
        },
        { enableHighAccuracy: true, maximumAge: 5_000, timeout: 30_000 },
      );
    }
    // a phone that stands still reports nothing new: tell the last position again now and then
    const still = setInterval(() => {
      if (latest !== null && worthTelling(last, latest, Date.now())) tell(latest);
    }, 15_000);

    // in the browser keep the screen on during a ride where it can: a dark screen stops the position
    let lock: WakeLockSentinel | null = null;
    const keepAwake = () => {
      if (plugin !== null) return; // the app goes on in the background, the screen may go dark
      if (stopped || document.visibilityState !== "visible" || !("wakeLock" in navigator)) return;
      navigator.wakeLock
        .request("screen")
        .then((sentinel) => {
          if (stopped) void sentinel.release();
          else lock = sentinel;
        })
        .catch(() => undefined);
    };
    keepAwake();
    document.addEventListener("visibilitychange", keepAwake);

    function stop() {
      if (stopped) return;
      stopped = true;
      if (watch !== null) navigator.geolocation.clearWatch(watch);
      if (nativeWatch !== null && plugin !== null) {
        void nativeWatch.then((id) => plugin.removeWatcher({ id })).catch(() => undefined);
      }
      clearInterval(still);
      document.removeEventListener("visibilitychange", keepAwake);
      void lock?.release().catch(() => undefined);
    }
    return () => {
      stop();
      // the next ride starts by looking again
      setHeard("looking");
    };
  }, [api, riding, able]);

  if (!riding || heard === "stopped") return "off";
  return able ? heard : "unavailable";
}
